// One 区間 (transport leg) card of the 出張旅費 form.
import {
  ArrowSquareOut, Receipt as ReceiptIcon, Trash, FileArrowUp, Copy,
} from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker, TimePicker } from '@/components/ui/native-datetime';
import { toast } from 'sonner';
import {
  API, RECEIPT_REQUIRED_MODES, HAS_LOOKUP_MODES, TRANSPORT_MODES, yahooUrl, yahooMissing,
} from './travelModel';

export const TransportLegCard = ({
  leg, idx, isSelected, canRemove, travelStartDate, travelEndDate, uploading,
  onToggleSelect, onCopy, onRemove, onUpdate, onReceiptUpload, onReceiptRemove,
}) => {
  const required = RECEIPT_REQUIRED_MODES.has(leg.mode);
  const hasLookup = HAS_LOOKUP_MODES.has(leg.mode);
  return (
    <div
      data-testid={`leg-${idx}`}
      className={`relative overflow-hidden rounded-xl border bg-white p-5 pl-6 shadow-sm transition-all hover:shadow-md ${isSelected ? 'border-teal-700/40 ring-1 ring-teal-700/20' : 'border-zinc-200 hover:border-[#B3CBFF]'}`}
    >
      {/* Left accent stripe — color reflects selection state */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${isSelected ? 'bg-teal-700' : 'bg-zinc-200'} transition-colors`} />
      <div className="flex items-center justify-between mb-3">
        <label className="inline-flex items-center gap-2 text-xs font-medium text-zinc-600 cursor-pointer select-none">
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            data-testid={`leg-checkbox-${idx}`}
          />
          <span className="uppercase tracking-wider text-zinc-500 font-semibold">区間 {idx + 1}</span>
        </label>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onCopy}
            data-testid={`leg-copy-${idx}`}
            className="p-1.5 text-zinc-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
            title="この区間を複製"
          >
            <Copy size={15} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={!canRemove}
            data-testid={`leg-remove-${idx}`}
            className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-zinc-500 transition-colors"
            title="この区間を削除"
          >
            <Trash size={15} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3">
          <Label className="text-xs text-zinc-600">交通手段</Label>
          <Select value={leg.mode} onValueChange={(v) => onUpdate({ mode: v })}>
            <SelectTrigger data-testid={`leg-mode-${idx}`} className="border-zinc-300 h-9 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSPORT_MODES.map((m) => {
                const Ic = m.icon;
                return (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="inline-flex items-center gap-2">
                      <Ic size={14} weight="duotone" />
                      {m.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        <div className="col-span-3">
          <Label className="text-xs text-zinc-600">出発地</Label>
          <Input
            value={leg.from}
            onChange={(e) => onUpdate({ from: e.target.value })}
            placeholder="例: 東京駅"
            data-testid={`leg-from-${idx}`}
            className="border-zinc-300 h-9 bg-white"
          />
        </div>

        <div className="col-span-3">
          <Label className="text-xs text-zinc-600">到着地</Label>
          <Input
            value={leg.to}
            onChange={(e) => onUpdate({ to: e.target.value })}
            placeholder="例: 新大阪駅"
            data-testid={`leg-to-${idx}`}
            className="border-zinc-300 h-9 bg-white"
          />
        </div>

        <div className="col-span-3">
          <Label className="text-xs text-zinc-600">運賃（円）</Label>
          <Input
            type="number"
            min="0"
            step="1"
            value={leg.fare}
            onChange={(e) => onUpdate({ fare: e.target.value })}
            data-testid={`leg-fare-${idx}`}
            className="border-zinc-300 h-9 bg-white text-right"
          />
        </div>
      </div>

      {/* Date + depart-time + arrive-time on one fixed-height line */}
      <div className="mt-2">
        <div className="flex items-center gap-2 rounded border border-zinc-200 bg-white px-2 h-12">
          <Label htmlFor={`leg-date-input-${idx}`} className="shrink-0 text-xs font-semibold text-zinc-600 select-none cursor-pointer">日付</Label>
          <DatePicker
            id={`leg-date-input-${idx}`}
            value={leg.date}
            min={travelStartDate}
            max={travelEndDate}
            onChange={(v) => onUpdate({ date: v })}
            testId={`leg-date-${idx}`}
            title="日付"
            className="flex-1 min-w-0"
          />
          <span className="shrink-0 text-xs font-bold text-emerald-700 px-1">発</span>
          <TimePicker
            value={leg.depart_time}
            onChange={(v) => onUpdate({ depart_time: v })}
            testId={`leg-depart-time-${idx}`}
            title="発時刻"
            className="flex-1 min-w-0"
          />
          <span className="shrink-0 text-xs font-bold text-blue-700 px-1">着</span>
          <TimePicker
            value={leg.arrive_time}
            onChange={(v) => onUpdate({ arrive_time: v })}
            testId={`leg-arrive-time-${idx}`}
            title="着時刻"
            className="flex-1 min-w-0"
          />
        </div>
      </div>

      {/* Lookup link — only for train/bus/shinkansen modes */}
      {hasLookup && (() => {
        const missing = yahooMissing(leg, travelStartDate);
        const ready = missing.length === 0;
        return (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-zinc-500">運賃を調べる:</span>
            <a
              href={ready ? yahooUrl(leg, travelStartDate) : '#'}
              target={ready ? '_blank' : undefined}
              rel="noopener noreferrer"
              onClick={(e) => {
                if (!ready) {
                  e.preventDefault();
                  toast.error(`${missing.join('・')}を入力してください`);
                }
              }}
              data-testid={`leg-yahoo-${idx}`}
              aria-disabled={!ready}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 border transition-colors ${ready ? 'text-blue-700 hover:text-blue-900 bg-white border-blue-300' : 'text-zinc-400 bg-zinc-100 border-zinc-200 cursor-not-allowed'}`}
            >
              Yahoo!路線情報で検索 <ArrowSquareOut size={11} />
            </a>
            {ready ? (
              <span className="text-zinc-500">
                （{leg.date || travelStartDate} {leg.depart_time ? `発 ${leg.depart_time}` : `着 ${leg.arrive_time}`} / 現金優先）
              </span>
            ) : (
              <span className="text-amber-700">未入力: {missing.join('・')}</span>
            )}
          </div>
        );
      })()}

      {/* Receipt upload — only for receipt-required modes */}
      {required && (
        <div className={`mt-2 rounded border px-3 py-2 ${(leg.receipts || []).length === 0 ? 'border-amber-300 bg-amber-50/60' : 'border-emerald-300 bg-emerald-50/40'}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs">
              <ReceiptIcon size={14} weight="duotone" className={(leg.receipts || []).length === 0 ? 'text-amber-700' : 'text-emerald-700'} />
              <span className="font-medium text-zinc-800">
                {leg.mode} の領収書 <span className="text-amber-700">*必須</span>
              </span>
              <span className="text-zinc-500">（PDF / 8MBまで）</span>
            </div>
            <label className="inline-flex items-center gap-1 text-xs font-medium text-zinc-700 hover:text-zinc-900 bg-white border border-zinc-300 rounded px-2 py-1 cursor-pointer transition-colors">
              <FileArrowUp size={13} />
              {uploading ? 'アップロード中…' : '領収書を追加'}
              <input
                type="file"
                accept="application/pdf"
                multiple
                onChange={onReceiptUpload}
                disabled={uploading}
                data-testid={`leg-receipt-input-${idx}`}
                className="hidden"
              />
            </label>
          </div>
          {(leg.receipts || []).length > 0 && (
            <ul className="mt-2 space-y-1" data-testid={`leg-receipt-list-${idx}`}>
              {leg.receipts.map((r) => (
                <li key={r.id} className="flex items-center gap-2 bg-white border border-zinc-200 rounded px-2 py-1">
                  {r.content_type?.startsWith('image/') ? (
                    <img src={`${API}/receipts/${r.id}`} alt={r.filename} className="w-8 h-8 object-cover rounded border border-zinc-200" />
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded border border-zinc-200">
                      <ReceiptIcon size={14} className="text-zinc-500" />
                    </div>
                  )}
                  <span className="flex-1 min-w-0 text-xs truncate">{r.filename}</span>
                  <span className="text-xs text-zinc-400">{Math.round((r.size || 0) / 1024)} KB</span>
                  <button
                    type="button"
                    onClick={() => onReceiptRemove(r.id)}
                    className="p-1 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
